import {
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Modal from 'react-bootstrap/Modal';
import { useParams } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Accordion from 'react-bootstrap/Accordion';
import { useRecoilValue, useRecoilState } from 'recoil';
import MDPService from '../services/mdp.service';
import {
  MDPData,
  PDPMatchData,
  PDPMatchResponseData,
  MatchAction,
  ActionData,
  ActionDefaultData,
  PresbyteryMatchingOptionData,
  MatchData,
  MatchDefaultData,
} from '../types/mdp.types';
import {
  mdpState,
  currentRoleState,
  presbyteryMatchingState,
  matchState,
} from '../services/state.service';
import ConfirmModal from '../components/generic/ConfirmModal';
import MDPMatchTable from '../components/MDPMatchTable';
import { Role } from '../types/user.types';
import {
  PDP_DISCERNMENT_ACTIONS,
  MINISTRY_ORG_TYPE,
  PRESBYTERY_MATCHING_OPTIONS,
  POSITIONTYPE_OPENTO_OPTIONS,
} from '../types/constants';
import api from '../services/api.service';
import CLCInput from '../components/generic/CLCInput';
import { canRequestRematch, FormatLocalDate } from '../utils';
import SubmitCallNotificationModal from '../components/SubmitCallNotificationModal';
import MatchCount from '../components/MatchCount';
import MDPNotesModal from '../components/MDPNotesModal';
import HelpPopup from '../components/generic/HelpPopup';
import RequestRematchModal from '../components/RequestRematchModal';
import MatchResponseModal from '../components/MatchResponseModal';
import SuspenseLoading from '../components/generic/SuspenseLoading';
import BackToDashboard from '../components/generic/BackToDashboard';

type MatchKey = 0 | 1 | 2 | 3 | 4;

function Matches(): JSX.Element {
  const params = useParams();
  const { t } = useTranslation();
  const [mdp, setMDP] = useRecoilState<MDPData>(mdpState);
  const [, setMatch] = useRecoilState<MatchData>(matchState);
  const [currentMatch, setCurrentMatch] = useState<MatchData>(MatchDefaultData);
  const [requireActionPdpMatches, setRequireActionPDPMatches] = useState<PDPMatchData[]>([]);
  const [selfReferralCount, setSelfReferralCount] = useState<number>(0);
  const [systemMatchCount, setSystemMatchCount] = useState<number>(0);
  const [presbySelfReferralCount, setPresbySelfReferralCount] = useState<number>(0);
  const [presbySystemMatchCount, setPresbySystemMatchCount] = useState<number>(0);
  const [discernmentPDPMatches, setDiscernmentPDPMatches] = useState<PDPMatchData[]>([]);
  const [notConsideredPDPMatches, setNotConsideredPDPMatches] = useState<PDPMatchData[]>([]);
  const [requirePresbyteryActionPDPMatches, setRequirePresbyteryActionPDPMatches] = useState<PDPMatchData[]>([]);
  const [presbyteryNotConsideredPDPMatches, setPresbyteryNotConsideredPDPMatches] = useState<PDPMatchData[]>([]);
  const [MatchActionOptions, setMatchActionOptionsData] = useState<MatchAction[]>([]);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [actionData, setActionData] = useState<ActionData>(ActionDefaultData);
  const [refresh, setRefresh] = useState(false);
  const [showMatchNotification, setShowMatchNotification] = useState(false);
  const [showMatchResponse, setShowMatchResponse] = useState(String);
  const [showRequestRematchModal, setShowRequestRematchModal] = useState(false);
  const [showNotifyCall, setShowNotifyCall] = useState(false);
  const currentRole = useRecoilValue<Role>(currentRoleState);
  const [matchingOptions, setMatchingOptions] = useRecoilState<PresbyteryMatchingOptionData>(presbyteryMatchingState);
  const [ShowSubmitting, setShowSubmitting] = useState(false);
  const [showMDPNotes, setShowMDPNotes] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<PDPMatchData>();
  const [sortBy, setSortBy] = useState<string>('1');
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [loadingState, setLoadingState] = useState({
    requireAction: true,
    discernment: true,
    notConsidered: true,
    requirePresbyteryAction: true,
    presbyteryNotConsidered: true,
  });
  const [showPresbyteryMatching, setShowPresbyteryMatching] = useState(false);
  // Add loading state for initial data fetch
  const [isLoading, setIsLoading] = useState(true);

  const fetchMatchData = useCallback((key: MatchKey) => {
    const setters: Record<MatchKey, (_data: PDPMatchData[]) => void> = {
      0: setRequireActionPDPMatches,
      1: setDiscernmentPDPMatches,
      2: setNotConsideredPDPMatches,
      3: setRequirePresbyteryActionPDPMatches,
      4: setPresbyteryNotConsideredPDPMatches,
    };

    const loadingKeys: Record<MatchKey, keyof typeof loadingState> = {
      0: 'requireAction',
      1: 'discernment',
      2: 'notConsidered',
      3: 'requirePresbyteryAction',
      4: 'presbyteryNotConsidered',
    };

    setLoadingState((prev) => ({ ...prev, [loadingKeys[key]]: true }));

    return MDPService.GetPDPMatches(params.id || '', key.toString())
      .then((response: PDPMatchResponseData) => {
        setters[key](response.data);
        if (key.toString() === '0') {
          setSelfReferralCount(response.data.filter((match) => match.selfReferred).length);
          setSystemMatchCount(response.data.filter((match) => !match.selfReferred).length);
        }
        if (key.toString() === '3') {
          setPresbySelfReferralCount(response.data.filter((match) => match.selfReferred).length);
          setPresbySystemMatchCount(response.data.filter((match) => !match.selfReferred).length);
        }
        setLoadingState((prev) => ({ ...prev, [loadingKeys[key]]: false }));
      });
  }, [params.id]);

  const fetchInitialData = useCallback(async () => {
    if (!params.id || !currentRole.id) return;

    try {
      setIsLoading(true);

      const [
        mdpResponse,
        actionOptionsResponse,
        matchDataResponse,
        presbyteryMatchingResponse,
      ] = await Promise.all([
        MDPService.GetMDPData(params.id),
        MDPService.GetActionOptions(),
        MDPService.GetMatchData(params.id),
        currentRole.organizationType !== MINISTRY_ORG_TYPE.other
          ? api.get(`presbyterymatching/${currentRole.organizationId}/`)
          : Promise.resolve({ data: { options: null } }),
      ]);

      // Set MDP data
      setMDP(mdpResponse.data);

      // Set action options
      setMatchActionOptionsData(actionOptionsResponse.data.options);

      // Set match data
      if (matchDataResponse.data) {
        const result = matchDataResponse.data[0];
        const matchData = {
          ...result,
          matchCharacteristics: result.matchCharacteristics.sort((a, b) => a.characteristic - b.characteristic),
        };
        setMatch(matchData);
        setCurrentMatch(matchData);
      }

      // Set presbytery matching options
      if (presbyteryMatchingResponse.data?.options) {
        setMatchingOptions(presbyteryMatchingResponse.data.options);
      }

      // Determine which sections should be active
      const initialActiveKeys: string[] = [];

      const matchByOrdainedPositions = presbyteryMatchingResponse.data?.options?.matchingChoice === PRESBYTERY_MATCHING_OPTIONS.ordained
        && (mdpResponse.data.openTo === POSITIONTYPE_OPENTO_OPTIONS.ordained || mdpResponse.data.openTo === POSITIONTYPE_OPENTO_OPTIONS.either);

      const matchByCommittee = presbyteryMatchingResponse.data?.options?.matchByCommittee?.includes(mdpResponse.data?.committee);

      // Check if presbytery matching should be shown
      const showPresbytery = currentRole.organizationType === MINISTRY_ORG_TYPE.presbytery
        && (matchByOrdainedPositions || matchByCommittee);

      if (showPresbytery) {
        initialActiveKeys.push('3');
        setShowPresbyteryMatching(true);
      }

      // Always show regular matching sections
      initialActiveKeys.push('0', '1');
      setActiveKeys(initialActiveKeys);

      setInitialized(true);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setIsLoading(false);
    }
  }, [params.id, currentRole.id, currentRole.organizationType, currentRole.organizationId]);

  // Use a single useEffect for initial data loading
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (initialized) {
      Promise.all(
        activeKeys.map((key: string) => fetchMatchData(key as unknown as MatchKey)),
      );
    }
  }, [refresh, initialized, fetchMatchData]);

  const handleAccordionToggle = useCallback((key: string) => {
    if (!activeKeys.includes(key)) {
      fetchMatchData(key as unknown as MatchKey);
    }

    setActiveKeys(
      activeKeys.includes(key)
        ? activeKeys.filter((k) => k !== key)
        : [...activeKeys, key],
    );
  }, [activeKeys, fetchMatchData]);

  const sortPDPMatches = (pdpMatchList: PDPMatchData[]): PDPMatchData[] => {
    switch (sortBy) {
      case '2':
        return pdpMatchList.sort((a, b) => b.score - a.score);
      case '3':
        return pdpMatchList.sort((a, b) => b.experienceLevelSortOrder - a.experienceLevelSortOrder);
      case '4':
        return pdpMatchList.sort((a, b) => a.pdp - b.pdp);
      case '5':
        return pdpMatchList.sort((a, b) => a.statusKey.localeCompare(b.statusKey));
      default:
        return pdpMatchList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
  };

  const submitActionData = async (result: boolean) : Promise<any> => {
    if (!result || !actionData) {
      setShowConfirmSubmit(false);
      setShowNotifyCall(false);
      return;
    }

    try {
      // Submit the action
      await MDPService.SubmitMatchAction(actionData);

      // Handle call notification if needed
      if (actionData.pdpId && actionData.actionKey === PDP_DISCERNMENT_ACTIONS.beingConsidered.notifyACall) {
        const resp = await MDPService.SaveCallNotification(mdp.id, actionData.pdpId.toString());
        actionData.id = resp;
        setShowSubmitting(true);
      }

      // Refresh data for all active sections
      await Promise.all(
        activeKeys.map((key) => fetchMatchData(key as unknown as MatchKey)),
      );

      // Reset UI states
      setShowConfirmSubmit(false);
      setShowNotifyCall(false);
      setRefresh((r) => !r);
    } catch (error) {
      console.error('Error submitting action:', error);
    }
  };

  const generateRematch = useCallback((result: boolean, match: MatchData): void => {
    if (result && match) {
      MDPService.UpdateMatchData(match).then((response: any) => {
        if (response.data) {
          const updatedMatchData = response.data;
          const matchData = {
            ...updatedMatchData,
            matchCharacteristics: updatedMatchData.matchCharacteristics.sort(
              (a:any, b:any) => a.characteristic - b.characteristic,
            ),
          };
          setMatch(matchData);
          setCurrentMatch(matchData);
        }
        setShowRequestRematchModal(false);
      }).then(() => {
        MDPService.GetPDPRematches(match.mdp.toString())
          .then((response: any) => {
            if (response.data === '0') {
              setShowMatchResponse(t('No_Match_Found') + response.data);
            } else {
              setShowMatchResponse(t('Matches_Found') + response.data);
            }

            setShowMatchNotification(true);
            setRefresh((r: boolean) => !r);
          });
      });
    } else {
      setShowRequestRematchModal(false);
    }
  }, []);

  const closeNewEdit = useCallback((result: boolean, data: PDPMatchData): void => {
    if (result && data) {
      MDPService.UpdatePDPMatch(data).then(() => {
        setShowMDPNotes(false);
        setSelectedMatch(undefined);
        setRefresh((r: boolean) => !r);
      });
    } else {
      setShowMDPNotes(false);
      setSelectedMatch(undefined);
    }
  }, []);

  function renderCompatibityScoreLegend(): JSX.Element {
    return (
      <div className="text-center mb-1">
        <span>
          {t('Compatibility_Score')}
          :
          <span className="circle green-circle me-1" />
          <span className="mb-1">{t('High')}</span>
          <span className="circle yellow-circle me-1" />
          <span className="mb-1">{t('Medium')}</span>
          <span className="circle red-circle me-1" />
          <span className="mb-1">{t('Low')}</span>
        </span>
      </div>
    );
  }

  function requestLimitReached(): boolean {
    return requireActionPdpMatches.filter((i) => (i.status === 'Requires action' && !i.selfReferred)
      || i.status === 'Invited to apply').length >= 25;
  }

  function discernmentLimitReached(): boolean {
    return discernmentPDPMatches.length >= 10;
  }

  // Show loading state while fetching initial data
  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="text-center">
          <SuspenseLoading />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <ConfirmModal
        show={showConfirmSubmit}
        title={t('PDP.Confirm')}
        description={t('Match_Confirmation')}
        yesLabel={t('PDP.Confirm')}
        noLabel={t('PDP.Cancel')}
        callback={submitActionData}
      />
      <SubmitCallNotificationModal
        show={ShowSubmitting}
        actionData={actionData}
        callback={setShowSubmitting}
      />
      <Modal
        show={showNotifyCall}
        onHide={() => setShowNotifyCall(false)}
        animation={false}
        className="modalstyle"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('Notify_Call')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="col-md-12 text-center my-5">
            <div>
              {t('Notify_Call_Description')}
            </div>
            <div className="mx-auto my-5 col-xs-12 col-sm-4">
              <CLCInput
                data={actionData}
                formLabel={t('PDP.Start_Date')}
                stateSetter={setActionData}
                inputName="start"
                inputType="date"
                maxLength={500}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="col-md-12 text-center">
            <Button
              className="px-5"
              variant="primary"
              disabled={!actionData.start}
              onClick={() => submitActionData(true)}
            >
              {t('OK')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      <Suspense fallback={<SuspenseLoading />}>
        <RequestRematchModal
          callback={generateRematch}
          show={showRequestRematchModal}
          hide={() => setShowRequestRematchModal(false)}
          mdp={mdp}
          currentMatch={currentMatch}
        />
      </Suspense>
      <MatchResponseModal
        show={showMatchNotification}
        hide={() => setShowMatchNotification(false)}
        showMatchResponse={showMatchResponse}
      />
      {selectedMatch && (
        <MDPNotesModal
          matchNote={selectedMatch}
          show={showMDPNotes}
          saveMDPNotes={closeNewEdit}
        />
      )}

      <div className="container-fluid">
        <div className="profile-panel accordion mb-3 mx-auto col-lg-11">
          <BackToDashboard />
          <div className="title text-center">
            {t('Matching_PDP_Results')}
          </div>
          <div className="mb-3 my-3 mx-auto mobiletable col-lg-12">
            <div className="row">
              {canRequestRematch(currentRole, matchingOptions, mdp) && (
                <div className="col-12">
                  <div className="d-flex">
                    <Button
                      onClick={() => setShowRequestRematchModal(true)}
                      disabled={requestLimitReached()}
                      className="mb-3 ms-auto createbutton d-flex"
                      variant="primary"
                      size="sm"
                      active
                    >
                      {`${t('Request_Rematch')}`}
                    </Button>
                    {requestLimitReached() && (
                      <HelpPopup
                        helpKey="tooltip-request-limit"
                        placement="left"
                        trigger={['hover', 'focus']}
                        content={t('Request_Limit_Reached')}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* InfoHeader */}
            <div className="matching-block p-3 mt-3 mb-3">
              <table>
                <tbody>
                  <tr>
                    <td className="fw-bold pe-3">
                      {t('View_Matches_MDP_ID')}
                      :
                    </td>
                    <td>
                      {mdp?.id}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold pe-3">
                      {t('Organization_Name')}
                      :
                    </td>
                    <td>
                      { `${mdp?.organizationName} (${mdp?.city}, ${mdp?.state})` }
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold pe-3">
                      {t('View_Matches_MDP_Position_Title')}
                      :
                    </td>
                    <td>
                      {mdp.positionType ? mdp.positionType : ''}
                      {mdp.positionTitle ? ` (${mdp.positionTitle})` : ''}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold pe-3">
                      {t('Released_Date')}
                      :
                    </td>
                    <td>
                      {mdp?.released ? FormatLocalDate(mdp.released) : ''}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Form.Group className="text-end">
              <Form.Label className="fw-bold pe-2">
                {t('Sort_By')}
                :
              </Form.Label>
              <Form.Select
                className="d-inline-block w-auto"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="1">{t('Date_Match')}</option>
                <option value="2">{t('Compatibility_Score')}</option>
                <option value="3">{t('ExperienceLevel')}</option>
                <option value="4">{t('PDP_ID')}</option>
                <option value="5">{t('Status')}</option>
              </Form.Select>
            </Form.Group>
            {/* Presbytery Matching */}
            {/* Current Recent PDP Matches */}
            {showPresbyteryMatching && (
              <div>
                {loadingState.requirePresbyteryAction ? (
                  <div className="text-center mb-3">
                    <SuspenseLoading />
                  </div>
                ) : (
                  <>
                    {renderCompatibityScoreLegend()}
                    <Accordion
                      activeKey={activeKeys}
                      onSelect={(key) => handleAccordionToggle(key as string)}
                    >
                      <Accordion.Item eventKey="3">
                        <Accordion.Button style={{ background: '#D1FFBD' }}>
                          <span className="accordion-header-label">
                            {t('Requires_Presbytery_Action')}
                          </span>
                        </Accordion.Button>
                        <Accordion.Body>
                          {loadingState.requirePresbyteryAction ? (
                            <div className="text-center mb-3">
                              <SuspenseLoading />
                            </div>
                          ) : (
                            <>
                              <MatchCount
                                selfReferralCount={presbySelfReferralCount}
                                count={presbySystemMatchCount}
                                max={25}
                              />
                              <div className="panel-header">
                                <ConfirmModal
                                  show={showConfirmSubmit}
                                  title={t('PDP.Confirm')}
                                  description={t('Match_Confirmation')}
                                  yesLabel={t('PDP.Confirm')}
                                  noLabel={t('PDP.Cancel')}
                                  callback={submitActionData}
                                />
                                <div>
                                  <MDPMatchTable
                                    filteredPDPs={sortPDPMatches(requirePresbyteryActionPDPMatches)}
                                    pdpMatches={requirePresbyteryActionPDPMatches}
                                    setPDPMatches={setRequirePresbyteryActionPDPMatches}
                                    setActionData={setActionData}
                                    mdp={mdp}
                                    matchActionOptions={MatchActionOptions}
                                    setShowConfirmSubmit={setShowConfirmSubmit}
                                    setShowNotifyCall={setShowNotifyCall}
                                    setShowMDPNotes={setShowMDPNotes}
                                    setSelectedMatch={setSelectedMatch}
                                    state="requires presbytery action"
                                    isInvitationLimitReached={requestLimitReached()}
                                    isDiscernmentLimitReached={discernmentLimitReached()}
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* PDPs no longer considered */}
                      <Accordion.Item eventKey="4">
                        <Accordion.Button style={{ background: '#D1FFBD' }}>
                          <span className="accordion-header-label">
                            {t('Presbytery_Not_Considered')}
                          </span>
                        </Accordion.Button>
                        <Accordion.Body>
                          {loadingState.presbyteryNotConsidered ? (
                            <div className="text-center mb-3">
                              <SuspenseLoading />
                            </div>
                          ) : (
                            <div className="panel-header">
                              <ConfirmModal
                                show={showConfirmSubmit}
                                title={t('PDP.Confirm')}
                                description={t('Match_Confirmation')}
                                yesLabel={t('PDP.Confirm')}
                                noLabel={t('PDP.Cancel')}
                                callback={submitActionData}
                              />
                              <div>
                                <MDPMatchTable
                                  filteredPDPs={sortPDPMatches(presbyteryNotConsideredPDPMatches)}
                                  pdpMatches={presbyteryNotConsideredPDPMatches}
                                  setPDPMatches={setPresbyteryNotConsideredPDPMatches}
                                  setActionData={setActionData}
                                  mdp={mdp}
                                  matchActionOptions={MatchActionOptions}
                                  setShowConfirmSubmit={setShowConfirmSubmit}
                                  setShowNotifyCall={setShowNotifyCall}
                                  setShowMDPNotes={setShowMDPNotes}
                                  setSelectedMatch={setSelectedMatch}
                                  state="not considered by prebytery"
                                  isInvitationLimitReached={requestLimitReached()}
                                  isDiscernmentLimitReached={discernmentLimitReached()}
                                />
                              </div>
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                    <br />
                    <br />
                    <br />
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Regular Matching */}
        <div className="profile-panel accordion mb-3 mx-auto col-lg-11">
          <div className="mb-3 my-3 mx-auto mobiletable col-lg-12">
            {/* PDP Match Results Panels */}
            {/* Current Recent PDP Matches */}
            {renderCompatibityScoreLegend()}
            <Accordion
              activeKey={activeKeys}
              onSelect={(key) => handleAccordionToggle(key as string)}
            >
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <span className="accordion-header-label">
                    {t('PDP_Matches_Recent')}
                  </span>
                </Accordion.Header>
                <Accordion.Body>
                  {loadingState.requireAction ? (
                    <div className="text-center mb-3">
                      <SuspenseLoading />
                    </div>
                  ) : (
                    <>
                      <MatchCount
                        selfReferralCount={selfReferralCount}
                        count={systemMatchCount}
                        max={25}
                      />
                      <div className="panel-header">
                        <ConfirmModal
                          show={showConfirmSubmit}
                          title={t('PDP.Confirm')}
                          description={t('Match_Confirmation')}
                          yesLabel={t('PDP.Confirm')}
                          noLabel={t('PDP.Cancel')}
                          callback={submitActionData}
                        />
                        <div>
                          <MDPMatchTable
                            filteredPDPs={sortPDPMatches(requireActionPdpMatches)}
                            pdpMatches={requireActionPdpMatches}
                            setPDPMatches={setRequireActionPDPMatches}
                            setActionData={setActionData}
                            mdp={mdp}
                            matchActionOptions={MatchActionOptions}
                            setShowConfirmSubmit={setShowConfirmSubmit}
                            setShowNotifyCall={setShowNotifyCall}
                            setShowMDPNotes={setShowMDPNotes}
                            setSelectedMatch={setSelectedMatch}
                            state="requires action"
                            isInvitationLimitReached={requestLimitReached()}
                            isDiscernmentLimitReached={discernmentLimitReached()}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>

              {/* PDP Matches in review */}
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <span className="accordion-header-label">
                    {t('PDP_Matches_Review')}
                  </span>
                </Accordion.Header>
                <Accordion.Body>
                  {loadingState.discernment ? (
                    <div className="text-center mb-3">
                      <SuspenseLoading />
                    </div>
                  ) : (
                    <>
                      <MatchCount
                        count={discernmentPDPMatches.length}
                        max={10}
                      />
                      <div className="panel-header">
                        <ConfirmModal
                          show={showConfirmSubmit}
                          title={t('PDP.Confirm')}
                          description={t('Match_Confirmation')}
                          yesLabel={t('PDP.Confirm')}
                          noLabel={t('PDP.Cancel')}
                          callback={submitActionData}
                        />
                        <div>
                          <MDPMatchTable
                            displayName
                            filteredPDPs={sortPDPMatches(discernmentPDPMatches)}
                            pdpMatches={discernmentPDPMatches}
                            setPDPMatches={setDiscernmentPDPMatches}
                            setActionData={setActionData}
                            mdp={mdp}
                            matchActionOptions={MatchActionOptions}
                            setShowConfirmSubmit={setShowConfirmSubmit}
                            setShowNotifyCall={setShowNotifyCall}
                            setShowMDPNotes={setShowMDPNotes}
                            setSelectedMatch={setSelectedMatch}
                            state="discernment"
                            isInvitationLimitReached={requestLimitReached()}
                            isDiscernmentLimitReached={discernmentLimitReached()}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>

              {/* PDPs no longer considered */}
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <span className="accordion-header-label">
                    {t('PDP_Matches_Not_Considered')}
                  </span>
                </Accordion.Header>
                <Accordion.Body>
                  {loadingState.notConsidered ? (
                    <div className="text-center mb-3">
                      <SuspenseLoading />
                    </div>
                  ) : (
                    <div className="panel-header">
                      <ConfirmModal
                        show={showConfirmSubmit}
                        title={t('PDP.Confirm')}
                        description={t('Match_Confirmation')}
                        yesLabel={t('PDP.Confirm')}
                        noLabel={t('PDP.Cancel')}
                        callback={submitActionData}
                      />
                      <div>
                        <MDPMatchTable
                          filteredPDPs={sortPDPMatches(notConsideredPDPMatches)}
                          pdpMatches={notConsideredPDPMatches}
                          setPDPMatches={setNotConsideredPDPMatches}
                          setActionData={setActionData}
                          mdp={mdp}
                          matchActionOptions={MatchActionOptions}
                          setShowConfirmSubmit={setShowConfirmSubmit}
                          setShowNotifyCall={setShowNotifyCall}
                          setShowMDPNotes={setShowMDPNotes}
                          setSelectedMatch={setSelectedMatch}
                          state="not considered"
                          isInvitationLimitReached={requestLimitReached()}
                          isDiscernmentLimitReached={discernmentLimitReached()}
                        />
                      </div>
                    </div>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Matches;
